import {
  integer, maxLength, minLength, requiredIf,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { StringMap, TypeCampaignEnum } from '@/types';
import { atLeastOneSegmentValidator, includesValuesValidator, requiredWithMessage } from '@/helpers/CustomValidator';
import type { FlowOperatorMetadata, ValidationResult, NumberRange, Period } from '@/types/flow-types';
import Form from './Form.vue';

interface Locations {
  [key: string]: Location;
}

export interface Location {
  address: string;
  radius: string;
  lat: string;
  long: string;
}

export interface CustomerFilters {
  filtershopdatecreation?: {
    segment_number: number;
    time_unit_select: Period[];
    litteral_title: StringMap;
  };
  filtercreateaccountfrom?: {
    segment_number: number;
    time_unit_select: Period[];
    litteral_title: StringMap;
  };
  filtergender?: {
    segment_number: number;
    gender: StringMap[];
    litteral_title: StringMap;
  };
  filteragerange?: {
    segment_number: number;
    age_range: NumberRange;
    litteral_title: StringMap;
  };
  filtergroup?: {
    segment_number: number;
    groups: StringMap[];
    litteral_title: StringMap;
    operator: string[];
  };
  filterareas?: {
    segment_number: number;
    areas: Locations[];
    operator: string[];
  };
  filtercustomercountrie?: {
    segment_number: number;
    customers_countries_list: StringMap[];
    litteral_title: StringMap;
  };
  filterbirthdate?: {
    segment_number: number;
    time_unit_select: Period[];
    litteral_title: StringMap;
  };
  filtercreationdate?: {
    segment_number: number;
    time_unit_select: Period[];
    litteral_title: StringMap;
  };
  filterhasvouchernearexpiration?: {
    segment_number: number;
    time_unit_select: Period[];
    code_start: string[];
    litteral_title: StringMap;
  };
  filtercanreceivesms?: {
    segment_number: number;
    hidden: string;
    litteral_title: StringMap;
  };
  filtercanreceivefbmessenger?: {
    segment_number: number;
    hidden: string;
    litteral_title: StringMap;
  };
  filtercanreceivepushnotifications?: {
    segment_number: number;
    hidden: string;
    litteral_title: StringMap;
  };
  filtercustomersubscription?: {
    segment_number: number;
    hidden: string;
    litteral_title: StringMap;
  };
  filternewslettersubscription?: {
    segment_number: number;
    type_optin: StringMap[];
    litteral_title: StringMap;
  };
  filterhaschattedonfb?: {
    segment_number: number;
    time_unit_select: Period[];
    litteral_title: StringMap;
  };
  filterhascustomertag?: {
    segment_number: number;
    tags: StringMap[];
    operator: string[];
    litteral_title: StringMap;
  };
  filtermemberoflist?: {
    segment_number: number;
    lists: StringMap[];
    operator: string[];
    litteral_title: StringMap;
  };
  filterdeliverability?: {
    segment_number: number;
    time_unit_select: Period[];
    litteral_title: StringMap;
  };
}

export interface CustomerData {
  custom_title: string;
  custom_description: string;
  outputs_number: number;
  initial_number_outputs?: null | number;
  grant_additional_delay: string;
  extra_time: Period;
  inclusion: CustomerFilters;
  exclusion: CustomerFilters;
}

export function createDefaultData(data: Partial<CustomerData> = {}): CustomerData {
  return {
    custom_title: data.custom_title ?? '',
    custom_description: data.custom_description ?? '',
    outputs_number: data.outputs_number ?? 1,
    initial_number_outputs: data.initial_number_outputs,
    grant_additional_delay: data.grant_additional_delay ?? '0',
    extra_time: data.extra_time ?? { value: 1, unit: 'DAY' },
    inclusion: data.inclusion ?? {},
    exclusion: data.exclusion ?? {},
  };
}

const Customer: FlowOperatorMetadata<CustomerData> = {
  id: 'boxfiltre_3',
  kind: 'filter',
  label: 'automatedScenarios.filters.customers',
  icon: 'fa-cogs',
  hasConfiguration: true,
  // Deprecated: kept registered so existing flows continue to render and
  // validate, but the operator can no longer be inserted. The warning system
  // surfaces a shared "replace with Conditions logiques" message for all
  // deprecated filters.
  availableIn: [],

  incompatibilities: {
    [TypeCampaignEnum.AUTOMATION]: {
      nextOperators: ['boxabandonpanier', 'boxperiodiclaunching'],
    },
    [TypeCampaignEnum.BULK]: {
      nextOperators: ['boxchoosebulklist', 'boxaudiencesetup'],
    },
  },

  persistent: [
    { segmentId: 'filtershopdatecreation', boxId: '' },
    { segmentId: 'filtercreateaccountfrom', boxId: '' },
    { segmentId: 'filtercreatecartfrom', boxId: '' },
    { segmentId: 'filteragerange', boxId: '' },
    { segmentId: 'filtergroup', boxId: '' },
    { segmentId: 'filterhasvouchernearexpiration', boxId: '' },
    { segmentId: 'filtercustomersubscription', boxId: 'registeronsite' },
    { segmentId: 'filternewslettersubscription', boxId: 'subscribetonewsletter' },
  ],

  Form,

  defaultData: createDefaultData,

  outputs: (data: CustomerData) => data.outputs_number,

  getDisplayLabel(data: CustomerData): string | null {
    return data.custom_title || null;
  },

  getSubtitle(data: CustomerData): string | null {
    return data.custom_description || null;
  },

  async validate(data: CustomerData): Promise<ValidationResult> {
    // Normalize outputs_number to number
    const normalizedData = {
      ...data,
      outputs_number: Number(data.outputs_number),
    };

    const rules = {
      custom_title: {
        requiredWithMessage: requiredWithMessage(),
        minLength: minLength(3),
        maxLength: maxLength(45),
      },
      outputs_number: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator([1, 2]),
      },
      grant_additional_delay: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator(['0', '1']),
      },
      extra_time: {
        value: {
          required: requiredIf(() => data.grant_additional_delay === '1'),
          integer,
        },
        unit: {
          required: requiredIf(() => data.grant_additional_delay === '1'),
          includesValuesValidator: includesValuesValidator(['SECOND', 'MINUTE', 'HOUR', 'DAY']),
        },
      },
      inclusion: {
        required: atLeastOneSegmentValidator(data.exclusion),
      },
    };

    const v$ = useVuelidate(rules, normalizedData);
    const success = await v$.value.$validate();
    return {
      success,
      validate: v$,
    };
  },

  beforeInsert(data: CustomerData): CustomerData {
    const dataToReturn = { ...data };
    delete dataToReturn.initial_number_outputs;
    // TODO: Handle branch decrease when flow editor is ready
    return dataToReturn;
  },

  canBeDeleted(): boolean {
    return true;
  },
};

export default Customer;
